import React from 'react';

const PriceBreakdownTable = ({ breakdown, showDetailedView, pricingTier = 'retail' }) => {
  // Don't render if breakdown is empty or not provided
  if (!breakdown || Object.keys(breakdown).length === 0) {
    return null;
  }
  const formatCurrency = (value, defaultValue = "0.00") => {
    const num = parseFloat(value);
    return `£${(isNaN(num) ? parseFloat(defaultValue) : num).toFixed(2)}`;
  };

  // Helper to get tier label with discount percent
  const getTierDiscountLabel = () => {
    if (!breakdown.partner_tier_details) {
      return 'Discount';
    }
    const discountPercent = breakdown.partner_tier_details.discount_percent || 37.5;
    const tier = breakdown.pricing_tier_final_result || pricingTier;

    if (tier === 'choosyg') {
      return `Choosy Giant Discount (${discountPercent}%)`;
    } else if (tier === 'partner') {
      return `Partner Discount (${discountPercent}%)`;
    }
    return `Discount (${discountPercent}%)`;
  };

  // Helper to get tier price label
  const getTierPriceLabel = () => {
    const tier = breakdown.pricing_tier_final_result || pricingTier;
    if (tier === 'choosyg') {
      return 'Choosy Giant Price';
    } else if (tier === 'partner') {
      return 'Partner Price';
    }
    return 'Total Price';
  };
  
  // --- NCR Breakdown Display ---
  if (breakdown.is_ncr) {
    // Check if this NCR job uses the new curve margin pricing (retail/partner) or old volume discount pricing (trade)
    const useCurveMargin = breakdown.curve_margin_applied === true;

    return (
      <div className="mb-6">
        <h3 className="font-medium mb-3">Price Breakdown</h3>
        <div className="overflow-hidden border rounded">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Component</th>
                <th scope="col" className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Cost</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200 text-sm">
              {/* Material Costs Section (Iterate through NCR parts) */}
              {breakdown.material_cost_details && breakdown.material_cost_details.length > 0 && (
                <>
                  <tr className="bg-gray-50">
                    <td colSpan="2" className="px-4 py-2 font-medium">Material Costs</td>
                  </tr>
                  {useCurveMargin ? (
                    // NEW FLOW: Curve Margin (Retail/Partner) - Show base costs only
                    <>
                      {breakdown.material_cost_details.map((part, index) => (
                        <tr key={`ncr-material-${index}`}>
                          <td className="px-4 py-2 pl-8">Part {part.part_number}: {part.name} ({part.sheets_needed} sheets)</td>
                          <td className="px-4 py-2 text-right">{formatCurrency(part.base_cost_total)}</td>
                        </tr>
                      ))}
                      {/* Total Material Cost for NCR */}
                      <tr>
                        <td className="px-4 py-2 font-semibold">Total Material Cost</td>
                        <td className="px-4 py-2 text-right font-semibold">
                          {formatCurrency(breakdown.base_total_material_cost_before_markup ?? breakdown.material_cost_details.reduce((sum, part) => sum + (part.base_cost_total ?? 0), 0))}
                        </td>
                      </tr>
                    </>
                  ) : (
                    // OLD FLOW: Volume Discount (Trade) - Show detailed costs with markups
                    <>
                      {breakdown.material_cost_details.map((part, index) => (
                        <tr key={`ncr-material-${index}`}>
                          <td className="px-4 py-2 pl-8">Part {part.part_number}: {part.name} ({part.sheets_needed} sheets) {showDetailedView && part.base_cost ? `(Base: ${formatCurrency(part.base_cost)})` : ''}</td>
                          <td className="px-4 py-2 text-right">£{part.total_cost.toFixed(2)}</td>
                        </tr>
                      ))}
                      {/* Total Material Cost for NCR */}
                      <tr>
                        <td className="px-4 py-2 font-semibold">Total Material Cost</td>
                        <td className="px-4 py-2 text-right font-semibold">
                          {formatCurrency(breakdown.material_cost)}
                        </td>
                      </tr>
                    </>
                  )}
                </>
              )}

              {/* Print Cost for NCR */}
              <tr>
                <td className="px-4 py-2">Print Cost ({breakdown.total_sides_for_click} sides)</td>
                <td className="px-4 py-2 text-right">
                  {useCurveMargin ? formatCurrency(breakdown.base_click_cost ?? breakdown.click_cost) : formatCurrency(breakdown.click_cost)}
                </td>
              </tr>

              {/* Retail Margin Adjustment (NEW FLOW ONLY) */}
              {useCurveMargin && breakdown.base_cost_for_curve_margin !== undefined && (
                <>
                  <tr className="bg-blue-50"><td colSpan="2" className="px-4 py-2 font-semibold text-blue-700">Retail Margin Adjustment</td></tr>
                  <tr><td className="px-4 py-2 pl-8">Base Cost (Material + Print) for Curve</td><td className="px-4 py-2 text-right">{formatCurrency(breakdown.base_cost_for_curve_margin)}</td></tr>
                  <tr><td className="px-4 py-2 pl-8">Retail Curve Margin ({breakdown.curve_margin_percent?.toFixed(2)}%)</td><td className="px-4 py-2 text-right">+ {formatCurrency((breakdown.marked_up_cost_with_curve || 0) - (breakdown.base_cost_for_curve_margin || 0))}</td></tr>
                  <tr><td className="px-4 py-2 pl-8 font-medium">Total Material & Print (with Curve)</td><td className="px-4 py-2 text-right font-medium">{formatCurrency(breakdown.marked_up_cost_with_curve)}</td></tr>
                  <tr className="bg-blue-50"><td colSpan="2" className="px-4 py-1 text-xs text-blue-600 italic pl-8">Note: Curve margin replaces individual material/print markups.</td></tr>
                </>
              )}

              {/* Finishing costs for NCR (Use inner_finishes from breakdown_ncr) */}
              {breakdown.finishing_details?.inner_finishes && breakdown.finishing_details.inner_finishes.length > 0 && (
                <>
                  <tr className="bg-gray-50">
                    <td colSpan="2" className="px-4 py-2 font-medium">Finishing Costs</td>
                  </tr>
                  {breakdown.finishing_details.inner_finishes.map((process, index) => (
                    <React.Fragment key={`ncr-finish-${index}`}>
                      <tr>
                        <td className="px-4 py-2 pl-8">{process.name}</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(process.total_cost)}</td>
                      </tr>
                      {/* Wire Binding Edge Details */}
                      {process.process_id === 'wirebind' && breakdown.finishing_details?.inner_finishing_options?.wirebind?.binding_edge && (
                        <tr>
                          <td className="px-4 py-2 pl-12 text-xs text-gray-600">
                            Binding Edge: {breakdown.finishing_details.inner_finishing_options.wirebind.binding_edge.charAt(0).toUpperCase() + breakdown.finishing_details.inner_finishing_options.wirebind.binding_edge.slice(1)}
                          </td>
                          <td className="px-4 py-2 text-right"></td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </>
              )}
              {/* Misc costs for NCR */}
              {breakdown.misc_costs && breakdown.misc_costs.length > 0 && (
                <>
                  <tr className="bg-gray-50">
                    <td colSpan="2" className="px-4 py-2 font-medium">Additional Costs</td>
                  </tr>
                  {breakdown.misc_costs.map((cost, index) => (
                    <tr key={`ncr-misc-${index}`}>
                      <td className="px-4 py-2 pl-8">{cost.name}</td>
                      <td className="px-4 py-2 text-right">{formatCurrency(cost.cost)}</td>
                    </tr>
                  ))}
                </>
              )}
              {/* Setup cost, Min Charge, Total - These keys are the same for NCR */}
              <tr>
                <td className="px-4 py-2">Setup Cost</td>
                <td className="px-4 py-2 text-right">{formatCurrency(breakdown.setup_cost)}</td>
              </tr>
              
              {/* Minimum Charge Applied */}
              {breakdown.min_charge_applied && (
                <tr className="bg-yellow-50 text-yellow-800 text-xs">
                  <td className="px-4 py-1 italic" colSpan="2">Minimum charge applied</td>
                </tr>
              )}
              
              {/* Partner Tier Pricing for NCR */}
              {breakdown.partner_tier_details && (
                <>
                  {/* Retail Price */}
                  <tr className="border-b bg-gray-50">
                    <td className="py-3 font-semibold">Retail Price</td>
                    <td className="text-right font-semibold">£{breakdown.partner_tier_details.original_retail_price.toFixed(2)}</td>
                  </tr>
                  {/* Discount Row */}
                  <tr className="border-b text-green-600">
                    <td className="py-2">
                      {getTierDiscountLabel()}
                      <span className="block text-xs text-gray-500 normal-case">
                        Applied to final retail total
                      </span>
                    </td>
                    <td className="text-right">
                      -£{(breakdown.partner_tier_details.original_retail_price - breakdown.total_cost).toFixed(2)}
                    </td>
                  </tr>
                  {/* Tier Price */}
                  <tr className="font-bold bg-green-50 text-green-800">
                    <td className="py-3">{getTierPriceLabel()}</td>
                    <td className="text-right">£{breakdown.total_cost.toFixed(2)}</td>
                  </tr>
                </>
              )}
              
              {/* Standard Total for Non-Partner Tiers */}
              {!breakdown.partner_tier_details && (
                <tr className="bg-gray-50 font-bold">
                  <td className="px-4 py-3">TOTAL{breakdown.min_charge_applied ? ' (Min. Charge)' : ''}</td>
                  <td className="px-4 py-3 text-right">{formatCurrency(breakdown.total_cost)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
  // --- END NCR Breakdown Display ---
  
  // --- Standard/Booklet Detailed Breakdown ---
  if (showDetailedView) {
    // Determine if this is a retail/partner tier (which uses curve margin) vs trade tier (which uses volume discounts)
    const isRetailOrPartner = pricingTier === 'retail' || pricingTier === 'partner';
    
    // For retail/partner: use base costs and curve margin
    // For trade: use volume discounts and individual markups
    const baseMaterialCost = breakdown.material_cost || 0;
    const baseCoverCost = breakdown.cover_stock_details?.base_cost || 0;
    const baseClickCost = breakdown.base_total_click_cost || 0;
    
    // Trade tier calculations (for comparison)
    const discountedMaterialCost = breakdown.material_cost_discount?.final_cost_before_markup || baseMaterialCost;
    const materialMarkupValue = (breakdown.marked_up_material_cost || 0) - discountedMaterialCost;
    const discountedCoverCost = breakdown.cover_stock_details?.discount_info?.final_cost_before_markup || baseCoverCost;
    const coverMarkupValue = (breakdown.cover_stock_details?.total_cost || 0) - discountedCoverCost;
    const discountedClickCost = breakdown.click_cost_discount_info?.final_total_click_cost_before_markup || baseClickCost;
    const clickMarkupValue = (breakdown.click_cost || 0) - discountedClickCost;

    return (
      <div className="mb-6">
        <h3 className="font-medium mb-3">Detailed Price Breakdown</h3>
        <div className="overflow-hidden border rounded">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Component</th>
                <th scope="col" className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Cost</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200 text-sm">
              {/* Material Costs */}
              <tr className="bg-gray-100"><td colSpan="2" className="px-4 py-2 font-semibold">Material Costs</td></tr>
              
              {isRetailOrPartner ? (
                // Retail/Partner: Show base costs only
                <>
                  <tr><td className="px-4 py-2 pl-8">Inner Pages ({breakdown.paper_stock || 'N/A'}) - Base</td><td className="px-4 py-2 text-right">{formatCurrency(baseMaterialCost)}</td></tr>
                  {breakdown.cover_stock_details && (
                    <tr><td className="px-4 py-2 pl-8">Cover ({breakdown.cover_stock_details.name || 'N/A'}) - Base</td><td className="px-4 py-2 text-right">{formatCurrency(baseCoverCost)}</td></tr>
                  )}
                </>
              ) : (
                // Trade: Show detailed breakdown with volume discounts and markups
                <>
                  <tr><td className="px-4 py-2 pl-8">Inner Pages ({breakdown.paper_stock || 'N/A'}) - Base</td><td className="px-4 py-2 text-right">{formatCurrency(baseMaterialCost)}</td></tr>
                  {breakdown.material_cost_discount && (
                    <tr><td className="px-4 py-2 pl-12 text-xs text-gray-600">Volume Discount ({breakdown.material_cost_discount.discount_percent}%)</td><td className="px-4 py-2 text-right text-xs text-gray-600">- {formatCurrency(breakdown.material_cost_discount.discount_amount)}</td></tr>
                  )}
                  <tr><td className="px-4 py-2 pl-8">Inner Pages - Markup</td><td className="px-4 py-2 text-right">{formatCurrency(materialMarkupValue)}</td></tr>
                  <tr><td className="px-4 py-2 pl-8 font-medium">Inner Pages - Total Marked Up</td><td className="px-4 py-2 text-right font-medium">{formatCurrency(breakdown.marked_up_material_cost)}</td></tr>

                  {breakdown.cover_stock_details && (
                    <>
                      <tr><td className="px-4 py-2 pl-8">Cover ({breakdown.cover_stock_details.name || 'N/A'}) - Base</td><td className="px-4 py-2 text-right">{formatCurrency(baseCoverCost)}</td></tr>
                      {breakdown.cover_stock_details.discount_info && (
                        <tr><td className="px-4 py-2 pl-12 text-xs text-gray-600">Volume Discount ({breakdown.cover_stock_details.discount_info.discount_percent}%)</td><td className="px-4 py-2 text-right text-xs text-gray-600">- {formatCurrency(breakdown.cover_stock_details.discount_info.discount_amount)}</td></tr>
                      )}
                      <tr><td className="px-4 py-2 pl-8">Cover - Markup</td><td className="px-4 py-2 text-right">{formatCurrency(coverMarkupValue)}</td></tr>
                      <tr><td className="px-4 py-2 pl-8 font-medium">Cover - Total Marked Up</td><td className="px-4 py-2 text-right font-medium">{formatCurrency(breakdown.cover_stock_details.total_cost)}</td></tr>
                    </>
                  )}
                </>
              )}

              {/* Print Costs */}
              <tr className="bg-gray-100"><td colSpan="2" className="px-4 py-2 font-semibold">Print Costs ({breakdown.total_sides ?? breakdown.total_sides_for_click} sides)</td></tr>
              
              {isRetailOrPartner ? (
                // Retail/Partner: Show base costs only
                <>
                  {breakdown.base_inner_click_cost_details && <tr><td className="px-4 py-2 pl-8">Inner Pages Print - Base</td><td className="px-4 py-2 text-right">{formatCurrency(breakdown.base_inner_click_cost_details.cost)}</td></tr>}
                  {breakdown.base_cover_click_cost_details && <tr><td className="px-4 py-2 pl-8">Cover Print - Base</td><td className="px-4 py-2 text-right">{formatCurrency(breakdown.base_cover_click_cost_details.cost)}</td></tr>}
                  <tr><td className="px-4 py-2 pl-8">Total Print - Base</td><td className="px-4 py-2 text-right">{formatCurrency(baseClickCost)}</td></tr>
                </>
              ) : (
                // Trade: Show detailed breakdown with volume discounts and markups
                <>
                  {breakdown.base_inner_click_cost_details && <tr><td className="px-4 py-2 pl-8">Inner Pages Print - Base</td><td className="px-4 py-2 text-right">{formatCurrency(breakdown.base_inner_click_cost_details.cost)}</td></tr>}
                  {breakdown.base_cover_click_cost_details && <tr><td className="px-4 py-2 pl-8">Cover Print - Base</td><td className="px-4 py-2 text-right">{formatCurrency(breakdown.base_cover_click_cost_details.cost)}</td></tr>}
                  <tr><td className="px-4 py-2 pl-8">Total Print - Base</td><td className="px-4 py-2 text-right">{formatCurrency(baseClickCost)}</td></tr>
                  {breakdown.click_cost_discount_info && (
                    <tr><td className="px-4 py-2 pl-12 text-xs text-gray-600">Volume Discount ({breakdown.click_cost_discount_info.discount_percent}%)</td><td className="px-4 py-2 text-right text-xs text-gray-600">- {formatCurrency(breakdown.click_cost_discount_info.discount_amount)}</td></tr>
                  )}
                  <tr><td className="px-4 py-2 pl-8">Print - Markup</td><td className="px-4 py-2 text-right">{formatCurrency(clickMarkupValue)}</td></tr>
                  <tr><td className="px-4 py-2 pl-8 font-medium">Print - Total Marked Up</td><td className="px-4 py-2 text-right font-medium">{formatCurrency(breakdown.click_cost)}</td></tr>
                </>
              )}

              {/* Curve Margin (Retail/Partner) */}
              {isRetailOrPartner && breakdown.curve_margin_applied && (
                <>
                  <tr className="bg-blue-50"><td colSpan="2" className="px-4 py-2 font-semibold text-blue-700">Retail Margin Adjustment</td></tr>
                  <tr><td className="px-4 py-2 pl-8">Base Cost (Material + Print) for Curve</td><td className="px-4 py-2 text-right">{formatCurrency(breakdown.base_cost_for_curve_margin)}</td></tr>
                  <tr><td className="px-4 py-2 pl-8">Retail Curve Margin ({breakdown.curve_margin_percent?.toFixed(1)}%)</td><td className="px-4 py-2 text-right">+ {formatCurrency((breakdown.marked_up_cost_with_curve || 0) - (breakdown.base_cost_for_curve_margin || 0))}</td></tr>
                  <tr><td className="px-4 py-2 pl-8 font-medium">Total Material & Print (with Curve)</td><td className="px-4 py-2 text-right font-medium">{formatCurrency(breakdown.marked_up_cost_with_curve)}</td></tr>
                  <tr className="bg-blue-50"><td colSpan="2" className="px-4 py-1 text-xs text-blue-600 italic pl-8">Note: Curve margin replaces individual material/print markups.</td></tr>
                </>
              )}

                             {/* Finishing Costs */}
               {(breakdown.finishing_details?.inner_finishes?.length > 0 || breakdown.finishing_details?.cover_finishes?.length > 0) && (
                 <tr className="bg-gray-100"><td colSpan="2" className="px-4 py-2 font-semibold">Finishing Costs</td></tr>
               )}
               {breakdown.finishing_details?.inner_finishes?.map((process, index) => (
                 <React.Fragment key={`inner-finish-detail-${index}`}>
                   <tr>
                     <td className="px-4 py-2 pl-8">{process.name}</td>
                     <td className="px-4 py-2 text-right">{formatCurrency(process.total_cost)}</td>
                   </tr>
                   {/* Wire Binding Edge Details */}
                   {process.process_id === 'wirebind' && breakdown.finishing_details?.inner_finishing_options?.wirebind?.binding_edge && (
                     <tr>
                       <td className="px-4 py-2 pl-12 text-xs text-gray-600">
                         Binding Edge: {breakdown.finishing_details.inner_finishing_options.wirebind.binding_edge.charAt(0).toUpperCase() + breakdown.finishing_details.inner_finishing_options.wirebind.binding_edge.slice(1)}
                       </td>
                       <td className="px-4 py-2 text-right"></td>
                     </tr>
                   )}
                 </React.Fragment>
               ))}
               {breakdown.finishing_details?.cover_finishes?.map((process, index) => (
                 <tr key={`cover-finish-detail-${index}`}>
                   <td className="px-4 py-2 pl-8">{process.name} (Cover)</td>
                   <td className="px-4 py-2 text-right">{formatCurrency(process.total_cost)}</td>
                 </tr>
               ))}
               <tr><td className="px-4 py-2 font-semibold">Total Finishing Cost</td><td className="px-4 py-2 text-right font-semibold">{formatCurrency(breakdown.total_finishing_cost)}</td></tr>

              {/* Misc Costs & Setup */}
              <tr className="bg-gray-100"><td colSpan="2" className="px-4 py-2 font-semibold">Additional Costs</td></tr>
              {breakdown.misc_costs?.map((cost, index) => (
                <tr key={`misc-detail-${index}`}><td className="px-4 py-2 pl-8">{cost.name}</td><td className="px-4 py-2 text-right">{formatCurrency(cost.cost)}</td></tr>
              ))}
              <tr><td className="px-4 py-2 pl-8">Setup Cost</td><td className="px-4 py-2 text-right">{formatCurrency(breakdown.setup_cost)}</td></tr>

              {/* Subtotals & Final */}
              <tr className="bg-gray-100"><td colSpan="2" className="px-4 py-2 font-semibold">Summary</td></tr>
              <tr><td className="px-4 py-2">Subtotal (Before Job Type Multiplier)</td><td className="px-4 py-2 text-right">{formatCurrency(breakdown.curve_margin_applied ? breakdown.cost_before_job_type_multiplier_with_curve : breakdown.cost_before_job_type_multiplier)}</td></tr>
              {breakdown.job_type_multiplier_applied && (
                <tr><td className="px-4 py-2 pl-8">Job Type Multiplier ({breakdown.job_type_multiplier_value}x)</td><td className="px-4 py-2 text-right">{formatCurrency(breakdown.job_type_multiplier_impact)}</td></tr>
              )}
              <tr><td className="px-4 py-2 font-medium">Subtotal (Pre Min. Charge)</td><td className="px-4 py-2 text-right font-medium">{formatCurrency(breakdown.sub_total_cost)}</td></tr>
              {breakdown.min_charge_applied && (
                <tr className="bg-yellow-50 text-yellow-800 text-xs"><td className="px-4 py-1 italic" colSpan="2">Minimum charge of {formatCurrency(breakdown.min_charge_amount)} applied</td></tr>
              )}
              
              {/* Partner Tier Pricing */}
              {pricingTier === 'partner' && breakdown.partner_tier_details && (
                <>
                  {/* Retail Price */}
                  <tr className="border-b bg-gray-50">
                    <td className="py-3 font-semibold">Retail Price</td>
                    <td className="text-right font-semibold">£{breakdown.partner_tier_details.original_retail_price.toFixed(2)}</td>
                  </tr>
                  {/* Discount Row */}
                  <tr className="border-b text-green-600">
                    <td className="py-2">
                      {getTierDiscountLabel()}
                      <span className="block text-xs text-gray-500 normal-case">
                        Applied to final retail total
                      </span>
                    </td>
                    <td className="text-right">
                      -£{(breakdown.partner_tier_details.original_retail_price - breakdown.total_cost).toFixed(2)}
                    </td>
                  </tr>
                  {/* Tier Price */}
                  <tr className="font-bold bg-green-50 text-green-800">
                    <td className="py-3">{getTierPriceLabel()}</td>
                    <td className="text-right">£{breakdown.total_cost.toFixed(2)}</td>
                  </tr>
                </>
              )}
              
              {/* Standard Total for Non-Partner Tiers */}
              {pricingTier !== 'partner' && (
                <tr className="bg-gray-200 font-bold">
                  <td className="px-4 py-3">TOTAL{breakdown.min_charge_applied ? ' (Min. Charge)' : ''}</td>
                  <td className="px-4 py-3 text-right">{formatCurrency(breakdown.total_cost)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // --- Standard/Booklet Simple Breakdown (Existing Logic) ---
  return (
    <div className="mb-6">
      <h3 className="font-medium mb-3">Price Breakdown</h3>
      <div className="overflow-hidden border rounded">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Component</th>
              <th scope="col" className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Cost</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200 text-sm">
            {/* --- Material Costs Section --- */}
            {(breakdown.marked_up_material_cost > 0 || breakdown.cover_stock_details?.total_cost > 0) && ( // Show section if either cost exists
              <>
                <tr className="bg-gray-50">
                  <td colSpan="2" className="px-4 py-2 font-medium">Material Costs</td>
                </tr> {/* This row is for the section header */}
                {/* Inner Paper Cost */}
                {breakdown.marked_up_material_cost > 0 && (
                  <tr>
                    {/* Use breakdown.paper_stock which holds the name */}
                    <td className="px-4 py-2 pl-8">Inner Pages ({breakdown.paper_stock || 'N/A'})</td> {/* Ensure breakdown.paper_stock has the name */}
                    <td className="px-4 py-2 text-right">{formatCurrency(breakdown.marked_up_material_cost)}</td>
                  </tr>
                )}
                {/* Cover Stock Cost (Conditional) */}
                {breakdown.cover_stock_details && breakdown.cover_stock_details.total_cost > 0 && (
                  <tr>
                    <td className="px-4 py-2 pl-8">Cover ({breakdown.cover_stock_details.name || 'N/A'})</td>
                    <td className="px-4 py-2 text-right">{formatCurrency(breakdown.cover_stock_details.total_cost)}</td>
                  </tr>
                )}
                 {/* Total Material Cost for Standard/Booklet */}
                 <tr>
                    <td className="px-4 py-2 font-semibold">Total Material Cost</td>
                    <td className="px-4 py-2 text-right font-semibold">
                      £{(
                        (breakdown.marked_up_material_cost || 0) +
                        (breakdown.cover_stock_details?.total_cost || 0)
                      ).toFixed(2)} {/* This calculation is fine for display */}
                    </td>
                  </tr>
              </>
            )}

            {/* --- Folding Information Section --- */}
            {breakdown.is_folded && (
              <>
                <tr className="bg-blue-50">
                  <td colSpan="2" className="px-4 py-2 font-medium text-blue-800">Folding Information</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 pl-8">Fold Type</td>
                  <td className="px-4 py-2 text-right">
                    {breakdown.fold_type.charAt(0).toUpperCase() + breakdown.fold_type.slice(1)} Fold
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-2 pl-8">Finished Size</td>
                  <td className="px-4 py-2 text-right">{breakdown.finished_dimensions}</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 pl-8">Flat Size (Unfolded)</td>
                  <td className="px-4 py-2 text-right">{breakdown.unfolded_dimensions}</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 pl-8">Panels</td>
                  <td className="px-4 py-2 text-right">{breakdown.panels}</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 pl-8">Effective Page Count</td>
                  <td className="px-4 py-2 text-right">
                    {breakdown.effective_page_count} {breakdown.effective_page_count === 1 ? 'page' : 'pages'}
                  </td>
                </tr>
              </>
            )}

            {/* Click cost */}
            <tr>
              <td className="px-4 py-2">Print Cost ({breakdown.total_sides ?? breakdown.total_sides_for_click} sides)</td> {/* Use total_sides for standard, total_sides_for_click for NCR (fallback) */}
              <td className="px-4 py-2 text-right">{formatCurrency(breakdown.click_cost)}</td>
            </tr>
            {/* Finishing costs */}
            {breakdown.finishing && breakdown.finishing.length > 0 && (
              <>
                <tr className="bg-gray-50">
                  <td colSpan="2" className="px-4 py-2 font-medium">Finishing Costs</td>
                </tr>
                {breakdown.finishing.map((process, index) => (
                  <React.Fragment key={index}>
                    <tr>
                      <td className="px-4 py-2 pl-8">{process.name}</td>
                      <td className="px-4 py-2 text-right">{formatCurrency(process.total_cost)}</td>
                    </tr>
                    {/* Wire Binding Edge Details */}
                    {process.process_id === 'wirebind' && breakdown.finishing_details?.inner_finishing_options?.wirebind?.binding_edge && (
                      <tr>
                        <td className="px-4 py-2 pl-12 text-xs text-gray-600">
                          Binding Edge: {breakdown.finishing_details.inner_finishing_options.wirebind.binding_edge.charAt(0).toUpperCase() + breakdown.finishing_details.inner_finishing_options.wirebind.binding_edge.slice(1)}
                        </td>
                        <td className="px-4 py-2 text-right"></td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </>
            )}
            {/* Misc costs */}
            {breakdown.misc_costs && breakdown.misc_costs.length > 0 && (
              <>
                <tr className="bg-gray-50">
                  <td colSpan="2" className="px-4 py-2 font-medium">Additional Costs</td>
                </tr>
                {breakdown.misc_costs.map((cost, index) => (
                  <tr key={index}>
                    <td className="px-4 py-2 pl-8">{cost.name}</td>
                    <td className="px-4 py-2 text-right">{formatCurrency(cost.cost)}</td>
                  </tr>
                ))}
              </>
            )}
            {/* Setup cost */}
            <tr>
              <td className="px-4 py-2">Setup Cost</td> {/* Ensure breakdown.setup_cost is a number */}
              <td className="px-4 py-2 text-right">{formatCurrency(breakdown.setup_cost)}</td>
            </tr>
            {/* Minimum Charge Applied */}
            {breakdown.min_charge_applied && (
              <tr className="bg-yellow-50 text-yellow-800 text-xs">
                <td className="px-4 py-1 italic" colSpan="2">Minimum charge applied</td>
              </tr>
            )}
            {/* Partner Tier Pricing for Regular Jobs */}
            {breakdown.partner_tier_details && (
              <>
                {/* Retail Price */}
                <tr className="border-b bg-gray-50">
                  <td className="py-3 font-semibold">Retail Price</td>
                  <td className="text-right font-semibold">£{breakdown.partner_tier_details.original_retail_price.toFixed(2)}</td>
                </tr>
                {/* Partner Discount Row */}
                <tr className="border-b text-green-600">
                  <td className="py-2">
                    Partner Discount (37.5%)
                    <span className="block text-xs text-gray-500 normal-case">
                      Applied to final retail total
                    </span>
                  </td>
                  <td className="text-right">
                    -£{(breakdown.partner_tier_details.original_retail_price - breakdown.total_cost).toFixed(2)}
                  </td>
                </tr>
                {/* Partner Price */}
                <tr className="font-bold bg-green-50 text-green-800">
                  <td className="py-3">Partner Price</td>
                  <td className="text-right">£{breakdown.total_cost.toFixed(2)}</td>
                </tr>
              </>
            )}
            
            {/* Standard Total for Non-Partner Tiers */}
            {!breakdown.partner_tier_details && (
              <tr className="bg-gray-50 font-bold">
                <td className="px-4 py-3">TOTAL{breakdown.min_charge_applied ? ' (Min. Charge)' : ''}</td> {/* Indicate if min charge applied */}
                <td className="px-4 py-3 text-right">{formatCurrency(breakdown.total_cost)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Long Sheet Details Section */}
      {breakdown.sheet_type === "LONG_SHEET" && breakdown.long_sheet_details && (
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded text-sm">
          <h6 className="font-semibold mb-2 text-blue-900">Long-Feed Printing Details</h6>
          <dl className="grid grid-cols-2 gap-2 text-xs">
            <dt className="text-gray-600">Source Material:</dt>
            <dd className="font-medium">{breakdown.long_sheet_details.source_material}</dd>

            <dt className="text-gray-600">Trimmed To:</dt>
            <dd className="font-medium">{breakdown.long_sheet_details.trimmed_to}</dd>

            <dt className="text-gray-600">Printer:</dt>
            <dd className="font-medium">{breakdown.long_sheet_details.printer}</dd>

            <dt className="text-gray-600">Reason:</dt>
            <dd className="font-medium text-amber-700">{breakdown.long_sheet_details.reason}</dd>
          </dl>
        </div>
      )}
    </div>
  );
};

export default PriceBreakdownTable;